import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { shellDrawsNavigation } from '@/shell/shellDetection';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * The one element in the app that owns the window's height (I-0580).
 *
 * ⛔ **Nothing else may claim the viewport's height.** Before this existed, `Layout` did — it was
 * `h-screen h-[100dvh] overflow-hidden` — while `MaintenanceNotice` was mounted above it in
 * `App.tsx`. A bar and a full viewport stacked, so the document became the bar's height *plus* the
 * window, and everything past the fold was unreachable: the sidebar's footer (Agents,
 * Infrastructure, Settings, the account row) sat below the bottom edge and was simply cut off.
 *
 * The rule that replaces it is structural rather than arithmetic. This box is the height. Every top
 * bar is a `TOP_BAR_ROW` inside it, so it takes its own height *out* of the window by existing;
 * everything below the bars is `VIEWPORT_REMAINDER`, so it gets what is left. Nobody adds up bar
 * heights, nobody keeps an offset in sync, and a bar added next year shrinks the content without
 * its author having to know this file exists — the only thing they must do is mount it in here.
 *
 * ⚠️ That last part is the whole fix, so it is the part a test pins: `top-bar-shrinks-content.spec.ts`
 * asserts every `data-top-bar` and `data-bottom-bar` element is a descendant of `[data-app-viewport]`
 * and names any that is not. Mount the next bar outside this box and that test goes red pointing at it.
 *
 * I-2377: the same holds at the other edge. A bottom bar (`ViewportBottomBar`, `data-bottom-bar`) is
 * a `BOTTOM_BAR_ROW` drawn into the bottom-bar slot, so it too takes its height out of the window.
 * The slot is the shell's, just above the phone's tab bar, while `Layout` draws one
 * (`ShellBottomBarSlot`); everywhere else it is this box's own last row.
 *
 * I-2378: in the desktop app the title bar comes first. The Mac window has no system title bar, so
 * the tab row (`DesktopTabBar`) is the title bar, laid out around the traffic lights. Drawn below
 * the top bars, a bar ran under the window buttons, in the strip the window is dragged by. In the
 * desktop runtime this box's first row is therefore a title-bar slot (`data-title-bar-slot`), which
 * the tab bar draws into through `ViewportTitleBar` wherever it is mounted, and every top bar
 * follows it. A browser and the iPhone/iPad app have no slot.
 *
 * IU-1 (S-1): `documentScrolls` (from `useSinglePageScroller`) drops the viewport lock entirely —
 * the app's own shell, in phone mode, wants `document.scrollingElement` to be the one scroller, and
 * a box clamped to the window's height is exactly what stops that. Unset or `false`, nothing here
 * changes: every browser and desktop-app width keeps the fixed-viewport column this file exists for.
 */
/** At least the window's height: `100dvh` where it is understood, `100vh` where it is not. */
const FILLS_WINDOW = 'min-h-screen min-h-[100dvh]';

export function AppViewport({
  children,
  documentScrolls = false,
}: {
  children: ReactNode;
  documentScrolls?: boolean;
}) {
  const [viewportSlot, setViewportSlot] = useState<HTMLElement | null>(null);
  const [shellSlot, setShellSlot] = useState<HTMLElement | null>(null);
  const [titleBarSlot, setTitleBarSlot] = useState<HTMLElement | null>(null);
  const slots = useMemo(
    () => ({ slot: shellSlot ?? viewportSlot, setShellSlot, titleBarSlot }),
    [shellSlot, viewportSlot, titleBarSlot],
  );
  return (
    <div
      data-app-viewport=""
      /*
       * `h-screen` then `h-[100dvh]`: the second wins where dynamic viewport units are supported and
       * the first is the fallback where they are not. Not a duplicate — `100dvh` is what keeps the
       * bottom edge correct while a mobile browser's own chrome slides in and out.
       *
       * `relative` is load-bearing, carried down from I-0412: an absolutely positioned descendant
       * with no positioned ancestor is laid out against the document rather than against this box,
       * which neither `overflow-hidden` clips nor this height contains — it just makes the document
       * taller, which is the same failure this component exists to make impossible.
       */
      className={
        documentScrolls
          ? `relative flex flex-col bg-[#FEFEFE] text-gray-900 dark:bg-[#212121] dark:text-gray-100 ${
              /*
               * I-1309: where the document scrolls and the web draws its own bottom bar (a compact
               * iPad window), the page is at least the window, so the bar ends at the window's
               * bottom on a page shorter than it. Without the floor the box was its content's
               * height, and on Agents with nothing relaying the bar sat 440 pt down a 1057 pt
               * window. The iPhone app draws the native bar and keeps the content's height.
               */
              shellDrawsNavigation() ? '' : FILLS_WINDOW
            }`
          : 'relative flex h-screen h-[100dvh] flex-col overflow-hidden bg-[#FEFEFE] text-gray-900 dark:bg-[#212121] dark:text-gray-100'
      }
    >
      <ViewportSlotContext.Provider value={slots}>
        {isDesktopRuntime() ? (
          <div ref={setTitleBarSlot} data-title-bar-slot="" className="flex shrink-0 flex-col" />
        ) : null}
        {children}
        <div ref={setViewportSlot} data-bottom-bar-slot="viewport" className={BOTTOM_BAR_SLOT} />
      </ViewportSlotContext.Provider>
    </div>
  );
}

/**
 * What a top bar is: a row that cannot be squeezed, so it takes its height out of the viewport.
 *
 * Pair it with `data-top-bar="<name>"`, which is how the guard test finds the bar and how it names
 * one that has been mounted in the wrong place.
 */
export const TOP_BAR_ROW = 'shrink-0';

/**
 * What a bottom bar is (I-2377): the same unsqueezable row, at the window's bottom edge.
 *
 * Pair it with `data-bottom-bar="<name>"` and draw it through `ViewportBottomBar`. A status that
 * persists while the reader works (the maintenance notice) belongs here, where it reports without
 * moving the page: a top bar pushes the tab strip and the page down for as long as it shows, and in
 * the Mac app it ran under the window buttons.
 */
export const BOTTOM_BAR_ROW = 'shrink-0';

/**
 * What everything below the bars is: the remainder of the viewport, and never one pixel more.
 *
 * `min-h-0` is not optional. A flex item's default `min-height: auto` refuses to shrink below its
 * content, so `flex-1` alone still overflows the column — which is exactly how a screen ends up
 * taller than the window while looking, in the markup, as though it had been told not to be.
 */
export const VIEWPORT_REMAINDER = 'min-h-0 flex-1';

/**
 * A whole screen that is not the app shell — sign-in, register, connect, the reset-password pages.
 *
 * These used to be `min-h-screen`, which is the same defect as the one above wearing different
 * clothes: below a bar, a box with a minimum of the full viewport makes the document taller than
 * the window instead of fitting inside what is left. They take the remainder now, and scroll
 * within it.
 *
 * ⚠️ `safe center` rather than plain centring. When centred content is taller than its scroll box,
 * `align-items: center` overflows it in *both* directions and the top is unreachable — a
 * sign-in card on a short window would have its heading cut off with no way to scroll up to it.
 * `safe` falls back to `start` exactly in that case, and behaves as `center` the rest of the time.
 */
export const VIEWPORT_SCREEN = 'min-h-0 flex-1 overflow-y-auto [align-items:safe_center]';

/**
 * The slot's own box: a column of bottom bars, nothing drawn when it holds none.
 *
 * `sticky bottom-0` is for where the document scrolls (IU-1): the slot is the column's last row
 * there, so without it a bar would sit at the end of a long page rather than at the window's
 * bottom. Where `<main>` scrolls, the column never does, and sticky changes nothing.
 */
const BOTTOM_BAR_SLOT = 'sticky bottom-0 z-30 flex shrink-0 flex-col';

interface IViewportSlots {
  /** Where a bottom bar is drawn now: the shell's slot while it is mounted, else the viewport's. */
  slot: HTMLElement | null;
  setShellSlot: (slot: HTMLElement | null) => void;
  /** The desktop runtime's title-bar slot, the viewport's first row (I-2378); none elsewhere. */
  titleBarSlot: HTMLElement | null;
}

const ViewportSlotContext = createContext<IViewportSlots | null>(null);

/**
 * F-0781 (`docs/concept-mvp-evorix-etrun-migration.md`, "every shell banner is a bottom bar"): the shell's banners, in the order they stack in the
 * one bottom region, the state the reader is in first. Jay, 2026-11-01: "also on web any banners
 * should be on the bottom (e.g. run as, ungated preview,...)".
 */
export const BOTTOM_BAR_ORDER = {
  /** "You are viewing as …" with its Stop (F-0709), and the view's end. */
  asIf: 1,
  /** The session could not be verified (VerificationBanner). */
  verification: 2,
  /** Confirm your email; a bounced address; an erasure's grace period. */
  account: 3,
  /** The deployment is down or installing (I-2377). */
  maintenance: 4,
  /** "Ungated preview · <commit>" on dev. */
  preview: 5,
  /** "This is an example work area" (the examples gallery). */
  example: 6,
} as const;

export type BottomBarOrderType = keyof typeof BOTTOM_BAR_ORDER;

/**
 * A bar at the bottom of the app viewport (I-2377), drawn into the bottom-bar slot wherever in the
 * tree it is mounted, so one instance (and its state) serves the shell and the pre-account screens.
 * `order` places it among the others (F-0781), whichever mounted first: each order has its own
 * row of the slot, made in the order's place.
 *
 * Outside an `AppViewport` (a component test of the bar alone) it renders in place.
 */
export function ViewportBottomBar({
  children,
  order,
}: {
  children: ReactNode;
  order: BottomBarOrderType;
}) {
  const slots = useContext(ViewportSlotContext);
  const slot = slots?.slot ?? null;
  const [place, setPlace] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!slot) {
      setPlace(null);
      return undefined;
    }
    const held = bottomBarPlace(slot, order);
    setPlace(held);
    // After React has taken this bar out (or moved it to the other slot): an empty place goes.
    return () => {
      window.setTimeout(() => {
        if (!held.hasChildNodes()) held.remove();
      }, 0);
    };
  }, [slot, order]);
  if (!slots) return <>{children}</>;
  return place ? createPortal(children, place) : null;
}

/**
 * The slot's row for bars of `order`, made where its place in `BOTTOM_BAR_ORDER` puts it, so the
 * document's order is the one the reader sees and the last row is the one that ends the window
 * (`index.css` clears the home indicator under it).
 */
export function bottomBarPlace(slot: HTMLElement, order: BottomBarOrderType): HTMLElement {
  const rows = [...slot.children] as HTMLElement[];
  const found = rows.find((row) => row.dataset.bottomBarOrder === order);
  if (found) return found;
  const row = document.createElement('div');
  row.dataset.bottomBarOrder = order;
  row.className = 'flex shrink-0 flex-col';
  const after = rows.find(
    (other) =>
      BOTTOM_BAR_ORDER[other.dataset.bottomBarOrder as BottomBarOrderType] >
      BOTTOM_BAR_ORDER[order],
  );
  slot.insertBefore(row, after ?? null);
  return row;
}

/**
 * The desktop window's title bar (I-2378), drawn into the viewport's first row, above every top
 * bar, wherever in the tree it is mounted: the tab bar and its placeholder in `Layout`, the
 * signed-out drag strip in `App.tsx`. Where there is no slot (a browser, the iPhone/iPad app, a
 * component test) it renders in place.
 */
export function ViewportTitleBar({ children }: { children: ReactNode }) {
  const slots = useContext(ViewportSlotContext);
  if (!slots || !isDesktopRuntime()) return <>{children}</>;
  return slots.titleBarSlot ? createPortal(children, slots.titleBarSlot) : null;
}

/**
 * The shell's bottom-bar slot, and the tab bar under it: `Layout` draws this as the last row of the
 * page's column, so a bottom bar sits on the phone's tab bar rather than under it. The two stick to
 * the window's bottom together where the document scrolls, as the tab bar alone did before.
 *
 * While it is mounted its slot takes over from the viewport's own. With a `tabBar` the web's own bar
 * follows the slot and clears the home indicator for both (`data-above-tab-bar`, `index.css`). It
 * also publishes the bars' height as `--bottom-bars-height`, which the app's floating ＋ rises by, so
 * the ＋ never covers a bar's close button.
 */
export function ShellBottomBarSlot({ tabBar }: { tabBar?: ReactNode }) {
  const slots = useContext(ViewportSlotContext);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const setShellSlot = slots?.setShellSlot;
  useEffect(() => {
    if (!setShellSlot || !slot) return undefined;
    setShellSlot(slot);
    return () => setShellSlot(null);
  }, [setShellSlot, slot]);
  useEffect(() => {
    if (!slot || typeof ResizeObserver === 'undefined') return undefined;
    const root = document.documentElement;
    const measure = () =>
      root.style.setProperty('--bottom-bars-height', `${Math.round(slot.offsetHeight)}px`);
    measure();
    // The border box: a bar's clearance under it is a border (`index.css`).
    const observer = new ResizeObserver(measure);
    observer.observe(slot, { box: 'border-box' });
    return () => {
      observer.disconnect();
      root.style.removeProperty('--bottom-bars-height');
    };
  }, [slot]);
  return (
    <div data-bottom-stack="" className="sticky bottom-0 z-30 flex shrink-0 flex-col">
      <div
        ref={setSlot}
        data-bottom-bar-slot="shell"
        data-above-tab-bar={tabBar ? '' : undefined}
        className="flex shrink-0 flex-col"
      />
      {tabBar}
    </div>
  );
}
